import type { DynamicFilterItem } from "@/store/news-feed/filters/dynamic-filters.slice";

/**
 * Comparator: non-zero totals first (descending), zero-count items last.
 * @param {DynamicFilterItem} a
 * @param {DynamicFilterItem} b
 * @returns {number} Negative/zero/positive per the sort contract.
 */
const byTotalDesc = (a: DynamicFilterItem, b: DynamicFilterItem): number => {
  const aZero = a.total === 0 ? 1 : 0;
  const bZero = b.total === 0 ? 1 : 0;
  if (aZero !== bZero) return aZero - bZero;
  return b.total - a.total;
};

/**
 * Buckets list items by groupId; orphans (groupId absent from groupItemsMap) fall back to ungroupedLists.
 * Member lists within each bucket are sorted by {@link byTotalDesc}.
 * @param {DynamicFilterItem[]} listItems - All media list filter items to organise.
 * @param {Map<string, DynamicFilterItem>} groupItemsMap - Lookup map of known groups by id.
 * @returns {{ listsByGroupId: Map<string, DynamicFilterItem[]>; ungroupedLists: DynamicFilterItem[] }}
 */
export const buildMediaListHierarchy = (
  listItems: DynamicFilterItem[],
  groupItemsMap: Map<string, DynamicFilterItem>,
): {
  listsByGroupId: Map<string, DynamicFilterItem[]>;
  ungroupedLists: DynamicFilterItem[];
} => {
  const listsByGroupId = new Map<string, DynamicFilterItem[]>();
  const ungroupedLists: DynamicFilterItem[] = [];

  for (const item of listItems) {
    const gid = item.groupId;
    if (gid) {
      const bucket = listsByGroupId.get(gid) ?? [];
      bucket.push(item);
      listsByGroupId.set(gid, bucket);
    } else {
      ungroupedLists.push(item);
    }
  }

  // Orphan fallback: lists whose groupId has no matching group row → ungrouped
  for (const [gid, lists] of listsByGroupId) {
    if (!groupItemsMap.has(gid)) {
      ungroupedLists.push(...lists);
      listsByGroupId.delete(gid);
    }
  }

  // Sort member lists within each group and the ungrouped list.
  for (const [gid, lists] of listsByGroupId) {
    listsByGroupId.set(gid, [...lists].sort(byTotalDesc));
  }
  ungroupedLists.sort(byTotalDesc);

  return { listsByGroupId, ungroupedLists };
};

/**
 * Filters groups and ungrouped lists by searchTerm.
 * A group passes if its own label matches OR any member list label matches.
 * @param {DynamicFilterItem[]} groupItems - All group-level filter items.
 * @param {Map<string, DynamicFilterItem[]>} listsByGroupId - Member lists keyed by group id (from {@link buildMediaListHierarchy}).
 * @param {DynamicFilterItem[]} ungroupedLists - Lists with no parent group.
 * @param {string} searchTerm - Case-insensitive substring matched against labels; empty string returns all.
 * @returns {{ filteredGroups: DynamicFilterItem[]; filteredUngrouped: DynamicFilterItem[] }}
 */
export const filterMediaListHierarchy = (
  groupItems: DynamicFilterItem[],
  listsByGroupId: Map<string, DynamicFilterItem[]>,
  ungroupedLists: DynamicFilterItem[],
  searchTerm: string,
): {
  filteredGroups: DynamicFilterItem[];
  filteredUngrouped: DynamicFilterItem[];
} => {
  const sortedGroups = [...groupItems].sort(byTotalDesc);
  const sortedUngrouped = [...ungroupedLists].sort(byTotalDesc);

  if (!searchTerm) {
    return { filteredGroups: sortedGroups, filteredUngrouped: sortedUngrouped };
  }
  const lower = searchTerm.toLowerCase();
  const filteredGroups = sortedGroups.filter((g) => {
    if (g.label.toLowerCase().includes(lower)) return true;
    return (listsByGroupId.get(g.id) ?? []).some((l) =>
      l.label.toLowerCase().includes(lower),
    );
  });
  const filteredUngrouped = sortedUngrouped.filter((l) =>
    l.label.toLowerCase().includes(lower),
  );
  return { filteredGroups, filteredUngrouped };
};

/**
 * Merges groups and ungrouped lists into a single list sorted by {@link byTotalDesc},
 * then applies the TOP_LEVEL_PAGE_SIZE budget.
 * @param {DynamicFilterItem[]} filteredGroups - Groups to show, already filtered via {@link filterMediaListHierarchy}.
 * @param {DynamicFilterItem[]} filteredUngrouped - Ungrouped lists to show, already filtered.
 * @param {number} visibleTopCount - Maximum total top-level items to display.
 * @returns {{ visibleItems: DynamicFilterItem[] }}
 */
export const paginateMediaListHierarchy = (
  filteredGroups: DynamicFilterItem[],
  filteredUngrouped: DynamicFilterItem[],
  visibleTopCount: number,
): {
  visibleItems: DynamicFilterItem[];
} => {
  const merged = [...filteredGroups, ...filteredUngrouped].sort(byTotalDesc);
  return { visibleItems: merged.slice(0, visibleTopCount) };
};
